import React, { useState } from "react";
import { defaultUrl } from "../features/actions";
import { toast } from "react-hot-toast";
import { makePostRequest } from "../utilities/common";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Lock, Eye, EyeOff, RefreshCw, X } from "lucide-react";

const ChanPasssord = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    current_password: "",
    password: "",
    password_confirmation: "",
  });

  const cancelSave = () => {
    setFormData({
      current_password: "",
      password: "",
      password_confirmation: "",
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const saveNewPassword = async () => {
    if (
      formData.current_password.length < 3 ||
      formData.password.length < 3 ||
      formData.password_confirmation.length < 3
    ) {
      toast.error("Please fill in all password fields.");
      return;
    }

    if (formData.current_password === formData.password) {
      toast.error("New password cannot be the same as your old one.");
      return;
    }

    if (formData.password_confirmation !== formData.password) {
      toast.error("New passwords do not match.");
      return;
    }

    setLoading(true);
    const toastId = toast.loading("Updating security credentials...");

    try {
      await makePostRequest(`${defaultUrl}/update-password`, formData);
      toast.success("Security updated. Please log in again.", { id: toastId });

      cancelSave();
      window.sessionStorage.removeItem("token");

      setTimeout(() => {
        navigate("/");
      }, 2500);
    } catch (e: any) {
      toast.error("Update failed. Verify your old password and try again.", {
        id: toastId,
      });
    } finally {
      setLoading(false);
    }
  };

  const inputContainer =
    "relative flex items-center bg-slate-50 rounded-2xl border-2 border-transparent focus-within:border-blue-500 focus-within:bg-white transition-all px-5 py-4";
  const labelStyle =
    "text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1";

  return (
    <div className="max-w-2xl animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl">
            <ShieldCheck size={24} />
          </div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tight">
            Security Settings
          </h2>
        </div>
        <p className="text-slate-500 font-medium leading-relaxed">
          Update your password regularly to keep your administrative account
          secure.
        </p>
      </div>

      <div className="space-y-8">
        {/* Old Password */}
        <div className="space-y-1">
          <label className={labelStyle}>Current Password</label>
          <div className={inputContainer}>
            <Lock className="text-slate-300 mr-4" size={18} />
            <input
              name="current_password"
              type={showPassword ? "text" : "password"}
              className="w-full bg-transparent outline-none text-slate-700 font-bold"
              placeholder="••••••••"
              value={formData.current_password}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* New Password */}
          <div className="space-y-1">
            <label className={labelStyle}>New Password</label>
            <div className={inputContainer}>
              <RefreshCw className="text-slate-300 mr-4" size={18} />
              <input
                name="password"
                type={showPassword ? "text" : "password"}
                className="w-full bg-transparent outline-none text-slate-700 font-bold"
                placeholder="Min. 8 characters"
                value={formData.password}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Confirm New Password */}
          <div className="space-y-1">
            <label className={labelStyle}>Confirm New Password</label>
            <div className={inputContainer}>
              <RefreshCw className="text-slate-300 mr-4" size={18} />
              <input
                name="password_confirmation"
                type={showPassword ? "text" : "password"}
                className="w-full bg-transparent outline-none text-slate-700 font-bold"
                placeholder="Repeat new password"
                value={formData.password_confirmation}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* Visibility Toggle */}
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="flex items-center gap-2 text-xs font-black text-slate-400 uppercase tracking-widest hover:text-blue-600 transition-colors"
        >
          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          {showPassword ? "Hide Passwords" : "Show Passwords"}
        </button>

        {/* Actions */}
        <div className="flex items-center gap-4 pt-6">
          <button
            onClick={saveNewPassword}
            disabled={loading}
            className="flex-1 bg-slate-900 text-white h-14 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-slate-200 hover:bg-black hover:-translate-y-0.5 transition-all flex items-center justify-center gap-3"
          >
            {loading ? "Updating..." : "Update Password"}
          </button>

          <button
            onClick={cancelSave}
            className="px-8 h-14 rounded-2xl font-black text-xs uppercase tracking-widest text-slate-400 hover:bg-slate-50 transition-all"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Safety Note */}
      <div className="mt-12 p-6 bg-amber-50 rounded-[2rem] border border-amber-100 flex gap-4">
        <div className="p-2 bg-white rounded-xl h-fit text-amber-500 shadow-sm">
          <ShieldCheck size={20} />
        </div>
        <div>
          <h4 className="text-amber-900 font-black text-sm uppercase tracking-tight">
            Pro Tip
          </h4>
          <p className="text-amber-700/70 text-xs font-bold leading-relaxed mt-1">
            Use a combination of uppercase letters, numbers, and symbols to
            create a strong password that is hard to guess.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChanPasssord;
